import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Faq.css';
import { fetchExamTypes } from '../../../../api/examApi';
import { fetchCategories } from '../../../../api/categoryApi';
import { getFaq } from '../../../../api/faq';
import { toast } from 'react-toastify';
import { FaPlus, FaMinus, FaArrowLeft } from 'react-icons/fa';


function Faq() {
    const navigate = useNavigate();
    const [examTypes, setExamTypes] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [activeExamType, setActiveExamType] = useState('');
    const [expandedCategoryId, setExpandedCategoryId] = useState(null);
    const [expandedQuestion, setExpandedQuestion] = useState({});
    const [faqs, setFaqs] = useState([]);

    useEffect(() => {
        const loadCategories = async () => {
            try {
                const categories = await fetchCategories();
                setAllCategories(categories);
            } catch (error) {
                console.error('Error loading categories:', error);
                toast.error('Failed to load categories');
            }
        };
        loadCategories();
    }, []);

    useEffect(() => {
        const loadExamTypes = async () => {
            try {
                const types = await fetchExamTypes();
                setExamTypes(types);
                const posp = types.find((et) => et.examTypeDesc === 'POSP');
                // Set active exam type to 'POSP' if available, otherwise first type
                setActiveExamType(posp ? 'POSP' : types[0]?.examTypeDesc || '');
            } catch (error) {
                console.error('Error loading exam types:', error);
                toast.error('Failed to load exam types');
            }
        };
        loadExamTypes();
    }, []);

    // useEffect(() => {
    //     const fetchFaqData = async () => {
    //         try {
    //             const payload = {
    //                 search: "examType.examTypeId:eq:1",
    //                 sortBy: "question",
    //                 sortDirection: "asc",
    //                 page: 0,
    //                 size: 500,
    //             };

    //             const data = await getFaq(payload);
    //             setFaqs(data.content || []);
    //         } catch (error) {
    //             console.error('Error loading FAQs:', error);
    //             toast.error('Failed to load FAQs');
    //         }
    //     };

    //     fetchFaqData();
    // }, []);

    useEffect(() => {
        // don’t fire until examTypes are loaded and an active type is chosen
        if (!examTypes.length || !activeExamType) return;
      
        const fetchFaqData = async () => {
          // find the full examType object for the currently active description
          const selected = examTypes.find(et => et.examTypeDesc === activeExamType);
          if (!selected) return;  // nothing to do if no match
      
          try {
            const payload = {
              // inject the real ID instead of “1”
              search: `examType.examTypeId:eq:${selected.examTypeId}`,
              sortBy: "question",
              sortDirection: "asc",
              page: 0,
              size: 500,
            };
      
            const data = await getFaq(payload);
            setFaqs(data.content || []);
          } catch (error) {
            console.error('Error loading FAQs:', error);
            toast.error('Failed to load FAQs');
          }
        };
      
        fetchFaqData();
      }, [activeExamType, examTypes]);
      

    // Filter categories based on the active exam type
    const filteredCategories = allCategories.filter(
        (cat) => cat.examType?.examTypeDesc === activeExamType
    );

    // Toggle category open/close
    const handleToggleCategory = (categoryId) => {
        setExpandedCategoryId((prev) => (prev === categoryId ? null : categoryId));
    };

    // Toggle individual question open/close
    const handleToggleQuestion = (categoryId, questionIndex) => {
        setExpandedQuestion((prev) => {
            if (prev[categoryId] === questionIndex) {
                return { ...prev, [categoryId]: null };
            } else {
                return { ...prev, [categoryId]: questionIndex };
            }
        });
    };

    // Format category name in Title Case if certain exam types
    const formatCategoryName = (cat) => {
        const examDesc = cat.examType?.examTypeDesc;
        if (
            examDesc === 'NISM' ||
            examDesc === 'NISM V-A Live Webinar' ||
            examDesc === 'HEALTH PRODUCT TRAINING' ||
            examDesc === 'MOTOR PRODUCT TRAINING' ||
            examDesc === 'LIFE PRODUCT TRAINING'
        ) {
            return cat.categoryName
                .toLowerCase()
                .split(' ')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        }
        return cat.categoryName;
    };

    // Format exam type in Title Case if certain exam types
    const formatExamTypeDesc = (desc) => {
        if (
            desc === 'HEALTH PRODUCT TRAINING' ||
            desc === 'MOTOR PRODUCT TRAINING' ||
            desc === 'LIFE PRODUCT TRAINING'
        ) {
            return desc
                .toLowerCase()
                .split(' ')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        }
        return desc;
    };

    const handleBack = () => {
        navigate("/dashboard");
    }

    return (
        <div className="faq-container">
            <div className="mobile-back-button-container">
                <button className="mobile-back-button" onClick={handleBack}>
                    <FaArrowLeft />
                </button>
            </div>

            <div className='faq-heading-container'>
                <h2 className="faq-heading">Frequently Asked Question</h2>
                <p className="faq-subheading">Have questions? We're here to help.</p>
            </div>
            {/* Tabs */}
            <div className="faq-tabs">
                {examTypes.map((et) => (
                    <button
                        key={et.examTypeId}
                        className={`faq-tab ${activeExamType === et.examTypeDesc ? 'active' : ''}`}
                        onClick={() => setActiveExamType(et.examTypeDesc)}
                    >
                        {formatExamTypeDesc(et.examTypeDesc)}
                    </button>
                ))}
            </div>

            {/* FAQ Content */}
            <div className="faq-content">
                
                {filteredCategories.map((cat) => (
                    <div key={cat.categoryId} className="faq-category">
                        <div
                            className="faq-category-header"
                            onClick={() => handleToggleCategory(cat.categoryId)}
                        >
                            <span>{formatCategoryName(cat)}</span>
                            <span className="faq-dropdown-arrow">
                                {expandedCategoryId === cat.categoryId ? <FaMinus /> : <FaPlus />}
                            </span>
                        </div>

                        {expandedCategoryId === cat.categoryId && (
                            <div className="faq-category-body">
                                {faqs
                                    .filter((f) => f.category?.categoryId === cat.categoryId)
                                    .map((faq, index) => (
                                        <div
                                            key={index}
                                            className="faq-question"
                                            onClick={() => handleToggleQuestion(cat.categoryId, index)}
                                        >
                                            <div className="faq-question-header">
                                                Q: {faq.question}
                                                <span className="faq-dropdown-arrow">
                                                    {expandedQuestion[cat.categoryId] === index ? (
                                                        <FaMinus />
                                                    ) : (
                                                        <FaPlus />
                                                    )}
                                                </span>
                                            </div>
                                            {expandedQuestion[cat.categoryId] === index && (
                                                <div className="faq-answer">
                                                    <p>{faq.answer}</p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default Faq;
